// The risk and triage summary PDF (TGCLOUD-1910): the person's latest risk
// model results and each triage rule pack's result, with the pack's version,
// publication date, the published wording of every rule that fired, and the
// attribution and disclaimer its licence requires.
//
// Built as HTML and drawn by html2pdf (html2canvas into jsPDF), not with
// jsPDF's own text: jsPDF's standard fonts hold only WinAnsi, and the NHS
// directory's criteria use ≥ and ≤, which would print as other characters. A
// rule's wording must print as published.
//
// buildTriageReportHtml is pure; every value is escaped. Pack text is printed
// as written and never translated (triageText.js says why).
import { english } from 'src/i18n/language';
import { toLocalDateFromInstant } from 'src/util/partialDate';
import {
  dataNoteLines,
  eventsText,
  failureText,
  needText,
  outcomeText,
  refusalText,
  ruleOutcomeText
} from './triageText';
import { namerFor } from './triagePeople';
import {
  CLINRISK_DISCLAIMER,
  LICENCE_NOTICE,
  MODEL_NAME,
  TRADEMARK_NOTICE,
  qrisk3Summary
} from '../qrisk3/qrisk3Text';

const esc = (v) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const p = (text, cls) =>
  text ? `<p${cls ? ` class="${cls}"` : ''}>${esc(text)}</p>` : '';

const newest = (records, type) =>
  records.find((r) => r.recordType === type) || null;

// The latest result of each risk model, as the sidebar's tiles read it.
export const riskLines = (records, t = english) => {
  const lines = [];
  const gail = newest(records, 'gail');
  if (gail && gail.gailRiskScore != null)
    lines.push({
      model: 'GAIL',
      when: gail.createdAt,
      text: t('Risk score: {{gailRiskScore}}', {
        gailRiskScore: gail.gailRiskScore
      })
    });
  const canrisk = records.find(
    (r) => r.recordType === 'canrisk' && r.canriskResult
  );
  if (canrisk) {
    const pr = canrisk.canriskResult?.pedigree_result?.[0];
    const tenYr = pr?.ten_yr_cancer_risk?.[0]?.['breast cancer risk']?.percent;
    const lifetime =
      pr?.lifetime_cancer_risk?.[0]?.['breast cancer risk']?.percent;
    const mut = pr?.mutation_probabilties?.[0] || {};
    const genes = Object.entries(mut)
      .filter(([gene, v]) => gene !== 'no mutation' && v?.percent != null)
      .map(([gene, v]) => `${gene} ${v.percent}%`);
    const parts = [];
    if (tenYr != null && lifetime != null)
      parts.push(
        t('10yr: {{tenYr}}% | Lifetime: {{lifetime}}%', { tenYr, lifetime })
      );
    if (genes.length)
      parts.push(
        t('Carrier probabilities: {{genes}}', { genes: genes.join(', ') })
      );
    lines.push({
      model: 'CanRisk',
      when: canrisk.createdAt,
      text: parts.join('. ') || t('Scores calculated')
    });
  }
  const prs = records.find(
    (r) =>
      r.recordType === 'prs' &&
      (r.prsResult?.zscore != null || r.prsResult?.percentile != null)
  );
  if (prs) {
    const pr = prs.prsResult;
    const parts = [pr.trait || pr.pgsId].filter(Boolean);
    if (pr.zscore != null) parts.push(`z=${pr.zscore}`);
    if (pr.percentile != null)
      parts.push(
        t('{{percentile}}th percentile', { percentile: pr.percentile })
      );
    lines.push({ model: 'PRS', when: prs.createdAt, text: parts.join(', ') });
  }
  // QRISK3 (TGCLOUD-1911). Its disclaimer is printed beneath the table
  // whenever this line is (buildTriageReportHtml).
  const qrisk3 = records.find(
    (r) => r.recordType === 'qrisk3' && r.qrisk3Result
  );
  if (qrisk3)
    lines.push({
      model: MODEL_NAME,
      when: qrisk3.createdAt,
      text: qrisk3Summary(qrisk3.qrisk3Result, t),
      disclaimer: 'qrisk3'
    });
  return lines;
};

const packHtml = (pack, { t, fmtDate, lang, memberRecords, anonymous }) => {
  const nameOf = namerFor(pack.people, { memberRecords, anonymous, t });
  const packName = pack.shortName || pack.name || pack.packId;
  const out = [];
  out.push(`<div class="pack">`);
  out.push(`<h2>${esc(pack.name || packName)}</h2>`);
  out.push(
    p(
      pack.publicationDate
        ? t('Version {{version}}, {{publisher}}, published {{date}}', {
            version: pack.version,
            publisher: pack.publisher || '',
            date: fmtDate(pack.publicationDate)
          })
        : t('Version {{version}}, {{publisher}}', {
            version: pack.version,
            publisher: pack.publisher || ''
          }),
      'muted'
    )
  );
  if (pack.status === 'refused') {
    out.push(p(refusalText(pack.refusal, { t, pack: packName, lang })));
  } else {
    out.push(`<p class="outcome">${esc(outcomeText(pack.outcome, t))}</p>`);
    const fired = pack.fired || [];
    if (!fired.length) {
      out.push(
        p(
          pack.rulesEvaluated === 1
            ? t('The criterion is not met.')
            : t('None of the {{count}} criteria is met.', {
                count: pack.rulesEvaluated ?? 0
              })
        )
      );
    }
    for (const rule of fired) {
      out.push(`<div class="rule avoid-break">`);
      (rule.context || []).forEach((line) => out.push(p(line, 'muted')));
      out.push(p(rule.text, 'wording'));
      const outcome = ruleOutcomeText(rule.outcome, t);
      if (outcome) out.push(p(t('Outcome: {{outcome}}', { outcome })));
      if (rule.confirmations?.length) {
        out.push(p(t('Met on the record, subject to:')));
        rule.confirmations.forEach((c) => out.push(p(c, 'muted')));
      }
      if (rule.people?.length) {
        const who = rule.people.map((k) => {
          const ref = pack.people?.[k];
          const detail = ref ? eventsText(ref.events, ref.bilateral, t) : '';
          return detail
            ? t('{{person}}: {{diagnoses}}', {
                person: nameOf(k),
                diagnoses: detail
              })
            : nameOf(k);
        });
        out.push(p(t('Who meets it: {{people}}', { people: who.join('; ') })));
      }
      out.push(`</div>`);
    }
    const near = (pack.nearMisses || []).slice(0, 3);
    if (near.length) {
      out.push(`<h3>${esc(t('Closest criteria not met'))}</h3>`);
      near.forEach((rule) => {
        out.push(`<div class="rule avoid-break">`);
        out.push(
          p(
            t('Did not meet {{section}}, failed only on: {{failure}}', {
              section: rule.section || rule.ruleId,
              failure: failureText(rule.failedOn, { t, nameOf })
            })
          )
        );
        out.push(p(rule.text, 'muted'));
        out.push(`</div>`);
      });
    }
    const notEvaluated = pack.notEvaluated || [];
    if (notEvaluated.length) {
      out.push(
        p(
          notEvaluated.length === 1
            ? t('1 criterion could not be evaluated')
            : t('{{count}} criteria could not be evaluated', {
                count: notEvaluated.length
              })
        )
      );
      notEvaluated.forEach((rule) => {
        const needs = (rule.needs || []).map((n) => needText(n, { t, nameOf }));
        out.push(
          p(
            needs.length
              ? t('{{rule}}. Needs: {{needs}}', {
                  rule: rule.section || rule.ruleId,
                  needs: needs.join('; ')
                })
              : rule.section || rule.ruleId,
            'muted'
          )
        );
      });
    }
    const notes = (pack.dataNotes || []).flatMap((n) =>
      dataNoteLines(n, { t, nameOf })
    );
    if (notes.length) {
      out.push(
        `<h3>${esc(t('What the record holds that was not counted'))}</h3>`
      );
      notes.forEach((line) => out.push(p(line)));
    }
  }
  if (pack.routingNote) out.push(p(pack.routingNote, 'muted'));
  const licence = pack.licence || {};
  if (licence.displayRequired) {
    if (licence.attribution) out.push(p(licence.attribution, 'small'));
    if (licence.disclaimer) out.push(p(licence.disclaimer, 'small'));
  }
  out.push(`</div>`);
  return out.join('');
};

/**
 * @param {object} o
 * @param {{ name?: string, memberId?: string, upn?: string|number }} o.person
 * @param {string} [o.familyId]
 * @param {object[]} o.records    the person's risk records, newest first
 * @param {object|null} o.evaluation  GET /triage/evaluate's answer
 * @param {Function} [o.t]
 * @param {Function} o.fmtDate    useDateFormatter's
 * @param {string} [o.lang]
 * @param {object[]} [o.memberRecords]
 * @param {boolean} [o.anonymous]
 * @param {Date} [o.now]
 * @returns {string} an HTML fragment
 */
export const buildTriageReportHtml = ({
  person = {},
  familyId,
  records = [],
  evaluation,
  t = english,
  fmtDate,
  lang,
  memberRecords,
  anonymous = true,
  now = new Date()
}) => {
  const out = [];
  out.push(`<div class="report">`);
  out.push(`<style>
    .report { font-family: Arial, Helvetica, sans-serif; color: #1f2733; font-size: 11px; line-height: 1.4; }
    .report h1 { font-size: 18px; margin: 0 0 4px; }
    .report h2 { font-size: 14px; margin: 14px 0 2px; }
    .report h3 { font-size: 12px; margin: 10px 0 2px; }
    .report p { margin: 2px 0; }
    .report .muted { color: #5a6475; }
    .report .small { color: #5a6475; font-size: 9px; }
    .report .outcome { font-weight: 700; font-size: 12px; margin: 4px 0 6px; }
    .report .wording { white-space: pre-wrap; }
    .report .rule { border: 1px solid #d6dbe3; border-radius: 4px; padding: 6px 8px; margin: 4px 0; }
    .report table { border-collapse: collapse; width: 100%; margin-top: 4px; }
    .report td, .report th { border-bottom: 1px solid #e3e7ed; padding: 3px 4px; text-align: left; vertical-align: top; }
  </style>`);
  out.push(`<h1>${esc(t('Risk and triage summary'))}</h1>`);
  const who = [
    anonymous ? '' : person.name,
    person.memberId,
    person.upn !== undefined && person.upn !== null && person.upn !== ''
      ? t('UPN {{upn}}', { upn: person.upn })
      : ''
  ]
    .filter(Boolean)
    .join(', ');
  out.push(p(who));
  if (familyId) out.push(p(t('Family {{family}}', { family: familyId })));
  out.push(p(t('Produced {{date}}', { date: fmtDate(now) }), 'muted'));

  out.push(`<h2>${esc(t('Risk models'))}</h2>`);
  const risks = riskLines(records, t);
  if (!risks.length) {
    out.push(p(t('No risk model results are recorded for this person.')));
  } else {
    out.push(`<table><tbody>`);
    risks.forEach((r) => {
      out.push(
        `<tr><th>${esc(r.model)}</th><td>${esc(r.text)}</td><td>${esc(
          r.when ? fmtDate(toLocalDateFromInstant(r.when)) : ''
        )}</td></tr>`
      );
    });
    out.push(`</tbody></table>`);
    // ClinRisk's disclaimer, held together with the QRISK3 score as its
    // licence requires: verbatim, never translated.
    if (risks.some((r) => r.disclaimer === 'qrisk3')) {
      out.push(`<div class="avoid-break">`);
      out.push(
        `<h3>${esc(
          t('{{model}}: disclaimer from ClinRisk Ltd', { model: MODEL_NAME })
        )}</h3>`
      );
      out.push(p(CLINRISK_DISCLAIMER, 'small'));
      out.push(p(t(LICENCE_NOTICE), 'small'));
      out.push(p(TRADEMARK_NOTICE, 'small'));
      out.push(`</div>`);
    }
  }

  out.push(`<h2>${esc(t('Triage'))}</h2>`);
  const packs = evaluation?.packs || [];
  if (!packs.length) {
    out.push(p(t('No triage rule packs are switched on for this group.')));
  } else {
    if (evaluation.evaluatedAt)
      out.push(
        p(
          t('Evaluated {{date}}, engine {{engine}}', {
            date: fmtDate(toLocalDateFromInstant(evaluation.evaluatedAt)),
            engine: evaluation.engineVersion || ''
          }),
          'muted'
        )
      );
    packs.forEach((pack) =>
      out.push(packHtml(pack, { t, fmtDate, lang, memberRecords, anonymous }))
    );
  }
  out.push(`</div>`);
  return out.join('');
};

/** Draw the summary and save it. html2pdf is loaded only when it is asked for. */
export const saveTriageReportPdf = async (html, filename) => {
  const { default: html2pdf } = await import('html2pdf.js');
  const element = document.createElement('div');
  element.innerHTML = html;
  return html2pdf()
    .set({
      margin: 12,
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'], avoid: ['.avoid-break'] }
    })
    .from(element)
    .save();
};
